import cx from 'classnames'
import Button from 'components/Button'
import ButtonCollection from 'components/ButtonCollection'
import { SideSheet } from 'components/SideSheet'
import {
  SideSheetHeader,
  SideSheetHeaderAvatar,
  SideSheetHeaderContent,
  SideSheetHeaderDescription,
  SideSheetHeaderText,
  SideSheetHeaderTitle,
} from 'components/SideSheetHeader'
import { useScrollOffsetSpring } from 'hooks/spring-hooks'
import { observer } from 'mobx-react'
import OpportunityDetailsViewModel from 'opportunities/OpportunityDetailsViewModel'
import * as React from 'react'
import NoOpportunitySelected from './NoOpportunitySelected'
import styles from './OpportunityDetailsSheet.sass'
import { formattedTimeUntil } from '../../../utils/date-util'
import {
  OpportunityDetailsContent,
  OpportunityDetailsContentItem,
  NoInfo,
} from '../../components/OpportunityDetailsContent'
import OpportunityDetailsActions from '../../components/OpportunityDetailsActions'
import Opportunity from 'opportunities/Opportunity'

const deadlineFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour12: true,
  hour: 'numeric',
  minute: 'numeric',
})

interface OpportunityDetailsSidebarProps {
  vm: OpportunityDetailsViewModel
  onCloseClick?: () => void
}

export default observer(function OpportunityDetailsSidebar({
  vm,
  onCloseClick,
}: OpportunityDetailsSidebarProps) {
  const rootRef = React.useRef<HTMLDivElement | null>(null)
  const scrollOffset = useScrollOffsetSpring(rootRef)
  const opportunity = vm.selected
  return (
    <SideSheet>
      <div className={cx(styles.root)} ref={rootRef}>
        {!opportunity ? (
          <NoOpportunitySelected />
        ) : (
          <>
            <SideSheetHeader
              bordered
              onCloseClick={onCloseClick}
              scrollOffset={scrollOffset}
            >
              <SideSheetHeaderContent>
                <SideSheetHeaderAvatar
                  cornerSubject={{
                    displayName: opportunity.opportunity.workspace.name,
                    avatar:
                      opportunity.opportunity.workspace.branding.logos.icon ||
                      require('assets/logos/logo_worklayer-blue.svg'),
                  }}
                  subject={
                    opportunity.opportunity.client.canViewInfo
                      ? opportunity.opportunity.client
                      : undefined
                  }
                />
                <SideSheetHeaderText>
                  <SideSheetHeaderTitle>
                    {opportunity.opportunity.name}
                  </SideSheetHeaderTitle>
                  <SideSheetHeaderDescription>
                    {opportunity.opportunity.scopeItems
                      .map((i) => i.name)
                      .join(' • ') || 'No description'}
                  </SideSheetHeaderDescription>
                </SideSheetHeaderText>
                <PickupActions vm={vm} />
              </SideSheetHeaderContent>
            </SideSheetHeader>
            <Content vm={vm} />
          </>
        )}
      </div>
    </SideSheet>
  )
})

interface PickupActionsProps {
  vm: OpportunityDetailsViewModel
}

const PickupActions = observer(function PickupActions({
  vm,
}: PickupActionsProps) {
  if (!vm.selected) {
    return null
  }

  return (
    <OpportunityDetailsActions>
      <ButtonCollection fullWidth>
        {vm.selected.canPickUpAsMyself && <LegacyPickupAction vm={vm} />}
        {vm.canShowApplyButton && <RoutingApplicationAction vm={vm} />}
      </ButtonCollection>
    </OpportunityDetailsActions>
  )
})

/**
 * The legacy, non-routing pickup action.
 */
function LegacyPickupAction({ vm }: { vm: OpportunityDetailsViewModel }) {
  return (
    <Button
      type="primary"
      // The args is for checking whether we called pickup with a team or
      // not. Based on that we show the spinner on the right button.
      // ＼（＾ ＾）／
      loading={vm.pickup.pending}
      disabled={vm.pickup.pending}
      onClick={() => vm.pickup()}
      dataTest="pickup-job-button"
      block
    >
      Pickup Job
    </Button>
  )
}

/**
 * The routing-based application action.
 */
const RoutingApplicationAction = observer(function RoutingApplicationAction({
  vm,
}: {
  vm: OpportunityDetailsViewModel
}) {
  return (
    <>
      <Button
        type={vm.applied ? 'danger' : 'primary'}
        loading={vm.applyWithdrawTasks.pending}
        disabled={vm.applyWithdrawTasks.pending}
        onClick={
          vm.applied
            ? vm.withdrawOpportunityApplication
            : vm.applyForOpportunity
        }
        dataTest={`job-${!vm.applied ? 'apply' : 'widthdraw'}-button`}
        block
      >
        {!vm.applied ? "I'm interested" : "I'm no longer interested"}
      </Button>
      {vm.selected?.opportunity.timeUntilSelection && (
        <p className={styles.selectionCountdown}>
          <span className={styles.text}>Selecting in</span>{' '}
          <span
            className={cx(
              vm.applied ? styles.countdownApplied : styles.countdown,
            )}
          >
            {formattedTimeUntil(vm.selected.opportunity.timeUntilSelection)}
          </span>
        </p>
      )}
    </>
  )
})

interface ContentProps {
  vm: OpportunityDetailsViewModel
}

const Content = observer(function Content({ vm }: ContentProps) {
  const op = vm.selected
  if (!op) {
    return null
  }

  const hasChildren =
    op.opportunity.infoLines.length ||
    op.opportunity.shouldShowProviderCutAmount ||
    (op.opportunity.shouldShowDeadline && op.opportunity.dateDeadline)

  const children = (
    <>
      {op.opportunity.infoLines.map((infoLine, idx) => (
        <OpportunityDetailsContentItem title={infoLine.title} key={idx}>
          {infoLine.content}
        </OpportunityDetailsContentItem>
      ))}
      {op.opportunity.shouldShowProviderCutAmount && (
        <OpportunityDetailsContentItem title="Total">
          {op.opportunity.formattedProviderCutAmount}
        </OpportunityDetailsContentItem>
      )}
      {op.opportunity.shouldShowDeadline && op.opportunity.dateDeadline && (
        <OpportunityDetailsContentItem title="Deadline" icon="clock">
          {deadlineFormatter.format(op.opportunity.dateDeadline)}
        </OpportunityDetailsContentItem>
      )}
      {op.opportunity.hasBookkeeping && (
        <OpportunityDetailsContentItem title="Service items">
          <ScopeItemsReceipt opportunity={op.opportunity} />
        </OpportunityDetailsContentItem>
      )}
    </>
  )

  return (
    <OpportunityDetailsContent>
      {hasChildren ? (
        children
      ) : (
        <NoInfo>
          <p className={styles.noInfo}>
            This opportunity is scarce on the details.
            <br />
            In other words, there's nothing to display here.
          </p>
        </NoInfo>
      )}
    </OpportunityDetailsContent>
  )
})

/**
 * Displays job scope items and their quantities.
 */
const ScopeItemsReceipt = observer(function ScopeItemsReceipt({
  opportunity,
}: {
  opportunity: Opportunity
}) {
  return opportunity.scopeItems.map((i) => (
    <>
      <div key={i.name} className={styles.scopeItem}>
        <div>{i.name}</div>
        <div className={styles.scopeItemCount}>{i.quantity}</div>
      </div>
    </>
  ))
})
